import React from 'react';

const WorksheetFilterPanel = ({
  filters,
  onChange,
  onReset,
  isCollapsed,
  onToggleCollapse
}) => {
  if (isCollapsed) return null;

  const handleInputChange = (field, value) => {
    onChange({
      ...filters,
      [field]: value,
      pageNumber: 1 // reset to first page on filter change
    });
  };

  const handleDatePreset = (preset) => {
    const today = new Date();
    let fromDate = new Date();
    let toDate = new Date();

    if (preset === 'yesterday') {
      fromDate.setDate(today.getDate() - 1);
      toDate.setDate(today.getDate() - 1);
    } else if (preset === 'week') {
      fromDate.setDate(today.getDate() - 7);
    }

    const fmt = (d) => d.toISOString().split('T')[0];

    onChange({
      ...filters,
      fromDate: fmt(fromDate),
      toDate: fmt(toDate),
      pageNumber: 1
    });
  };

  return (
    <div className="ws-top-filter-bar">
      {/* Primary Row: Search inputs, Section, Priority, Status */}
      <div className="ws-top-filter-row">
        {/* 1. Barcode / Quick Search */}
        <div className="ws-top-filter-col ws-col-search">
          <label className="ws-filter-label">Quick Search / Barcode</label>
          <div className="ws-barcode-input-wrapper">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 5v14M8 5v14M12 5v14M17 5v14M21 5v14" />
            </svg>
            <input
              type="text"
              className="ws-filter-input"
              placeholder="Barcode / Lab No / UHID..."
              value={filters.searchTerm || ''}
              onChange={(e) => handleInputChange('searchTerm', e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* 2. Test Name Search */}
        <div className="ws-top-filter-col ws-col-testname">
          <label className="ws-filter-label">Search by Test Name</label>
          <div className="ws-barcode-input-wrapper">
            <span style={{ fontSize: '13px', color: '#0284c7' }}>🧪</span>
            <input
              type="text"
              className="ws-filter-input"
              placeholder="Search Test (e.g. CBC, Lipid, Sugar)..."
              value={filters.testName || ''}
              onChange={(e) => handleInputChange('testName', e.target.value)}
            />
            {filters.testName && (
              <button
                type="button"
                className="ws-filter-clear-btn"
                onClick={() => handleInputChange('testName', '')}
                title="Clear test search"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* 3. Laboratory Section */}
        <div className="ws-top-filter-col ws-col-select">
          <label className="ws-filter-label">Laboratory Section</label>
          <select
            className="ws-filter-select"
            value={filters.subCategoryId || ''}
            onChange={(e) => handleInputChange('subCategoryId', e.target.value ? parseInt(e.target.value, 10) : null)}
          >
            <option value="">All Pathology Sections</option>
            <option value="1">Hematology</option>
            <option value="2">Biochemistry / Clinical Chemistry</option>
            <option value="3">Immunology / Immunoassay</option>
            <option value="4">Serology</option>
            <option value="5">Coagulation</option>
            <option value="6">Clinical Pathology / Urine</option>
          </select>
        </div>

        {/* 4. Priority Filter */}
        <div className="ws-top-filter-col ws-col-sm">
          <label className="ws-filter-label">Priority</label>
          <select
            className="ws-filter-select"
            value={filters.priority || ''}
            onChange={(e) => handleInputChange('priority', e.target.value || null)}
          >
            <option value="">All Priorities</option>
            <option value="STAT">⚡ STAT (Emergency)</option>
            <option value="Urgent">🔥 Urgent</option>
            <option value="Routine">Routine</option>
          </select>
        </div>

        {/* 5. Work Status */}
        <div className="ws-top-filter-col ws-col-sm">
          <label className="ws-filter-label">Work Status</label>
          <select
            className="ws-filter-select"
            value={filters.status || ''}
            onChange={(e) => handleInputChange('status', e.target.value || null)}
          >
            <option value="">All Active Statuses</option>
            <option value="Pending">Pending (Not Started)</option>
            <option value="In Progress">In Progress (Testing)</option>
            <option value="Hold">On Hold</option>
            <option value="Completed">Completed</option>
            <option value="Repeat">Repeat Required</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Secondary Row: Date Presets, Date Pickers, TAT Risk, and Reset */}
      <div className="ws-top-filter-row ws-top-filter-row-secondary">
        <div className="ws-top-date-group">
          <span className="ws-filter-label" style={{ margin: 0, fontWeight: 700, color: '#334155' }}>
            📅 Date:
          </span>
          <div className="ws-date-presets">
            <button
              type="button"
              className="ws-btn ws-btn-outline ws-btn-xs"
              onClick={() => handleDatePreset('today')}
            >
              Today
            </button>
            <button
              type="button"
              className="ws-btn ws-btn-outline ws-btn-xs"
              onClick={() => handleDatePreset('yesterday')}
            >
              Yesterday
            </button>
            <button
              type="button"
              className="ws-btn ws-btn-outline ws-btn-xs"
              onClick={() => handleDatePreset('week')}
            >
              7 Days
            </button>
          </div>
          <div className="ws-date-inputs">
            <input
              type="date"
              className="ws-filter-input ws-filter-input-sm"
              value={filters.fromDate || ''}
              onChange={(e) => handleInputChange('fromDate', e.target.value)}
            />
            <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
            <input
              type="date"
              className="ws-filter-input ws-filter-input-sm"
              value={filters.toDate || ''}
              onChange={(e) => handleInputChange('toDate', e.target.value)}
            />
          </div>
        </div>

        <div className="ws-top-filter-actions">
          {/* TAT Risk Checkbox */}
          <label className="ws-tat-risk-label" htmlFor="chk-tat-risk">
            <input
              type="checkbox"
              id="chk-tat-risk"
              checked={!!filters.tatRiskOnly}
              onChange={(e) => handleInputChange('tatRiskOnly', e.target.checked)}
            />
            <span>⏱️ TAT Risk Only (&lt; 30m / Overdue)</span>
          </label>

          {/* Reset Filters */}
          <button
            type="button"
            className="ws-btn ws-btn-outline ws-btn-sm"
            onClick={onReset}
            title="Reset all filter fields"
          >
            ↺ Reset
          </button>

          {/* Hide button */}
          <button
            type="button"
            className="ws-btn ws-btn-outline ws-btn-sm"
            onClick={onToggleCollapse}
            title="Hide Filter Panel"
            style={{ padding: '4px 8px' }}
          >
            ✕ Hide
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorksheetFilterPanel;
